import type { FeedItem, FeedMetadata, FeedStoreState } from '@knocklabs/client';
import { create } from 'zustand';

type FeedPageInfo = FeedStoreState['pageInfo'];

// Knock declares its fetch response as `data: any`, so narrowing it here stays
// assignable.
interface MessagesFeedPage {
  entries: FeedItem[];
  page_info: { after: null | string };
}

// Narrow view of Knock's feed client — only the members the app consumes. The
// real Knock `Feed` is structurally assignable to this, and the no-op default
// (used when Knock isn't configured, e.g. ephemeral/preview envs) satisfies it.
//
// Knock's `fetchNextPage` is omitted on purpose: it neither awaits nor returns the
// `fetch` it issues, so it settles before the page lands and the next cursor is
// unreadable. Paged reads call `fetch` directly instead.
interface MessagesFeedClient {
  fetch: (options?: {
    after?: string;
  }) => Promise<undefined | { status: 'error' | 'ok'; data: MessagesFeedPage }>;
  markAsRead: (item: FeedItem) => void;
  markAsArchived: (item: FeedItem) => void;
  // `unknown` because a rejected bulk resolves with the request error rather than
  // a BulkOperation — see `wasBulkAccepted`.
  markAllAsArchived: () => Promise<unknown>;
}

const noopClient: MessagesFeedClient = {
  fetch: () => Promise.resolve(undefined),
  markAsRead: () => undefined,
  markAsArchived: () => undefined,
  markAllAsArchived: () => Promise.resolve(),
};

// Knock reports a failed bulk archive two ways — a 4xx RESOLVES with the request
// error, a 5xx or network drop throws — and has already emptied its own store
// optimistically by then. Presence of the operation id is the only positive signal.
const wasBulkAccepted = (result: unknown): boolean =>
  typeof result === 'object' &&
  result !== null &&
  'id' in result &&
  typeof result.id === 'string';

// Mirrored from Knock's feed store: server-side totals across every page, plus an
// `after` cursor while more pages remain.
const defaultMetadata: FeedMetadata = {
  total_count: 0,
  unread_count: 0,
  unseen_count: 0,
};
const defaultPageInfo: FeedPageInfo = {
  before: null,
  after: null,
  page_size: 50,
};

// Knock caps a page at 50, so this is two pages — one fetch on top of the one the
// panel already has. The tail beyond it stays behind "Load more".
export const AUTO_LOAD_ITEM_LIMIT = 100;

interface MessagesFeedState {
  items: FeedItem[];
  loading: boolean;
  metadata: FeedMetadata;
  pageInfo: FeedPageInfo;
  client: MessagesFeedClient;
  // Re-entrancy guard for `fetchUpTo`, not a UI flag.
  loadingMore: boolean;
  // Mirror the live Knock feed into the store (called by the bridge).
  setFeed: (feed: {
    items: FeedItem[];
    loading: boolean;
    metadata: FeedMetadata;
    pageInfo: FeedPageInfo;
    client: MessagesFeedClient;
  }) => void;
  // Reset to an inert empty feed when Knock isn't configured. Also clears the
  // mirrored counts/cursor so a previous session's totals can't leak on re-login.
  reset: () => void;
  fetch: () => void;
  // Both resolve false only when a page genuinely failed — a request Knock
  // declined to issue resolves true.
  //
  // Resolves once the page has landed, so callers can show a real pending state.
  fetchNextPage: () => Promise<boolean>;
  // Walks pages until `limit` items are loaded or the cursor runs out.
  fetchUpTo: (limit: number) => Promise<boolean>;
  markAsRead: (item: FeedItem) => void;
  markAsArchived: (item: FeedItem) => void;
  // Resolves false when Knock rejected the bulk, so the caller can tell the user
  // and re-read the feed rather than trusting the optimistic clear.
  markAllAsArchived: () => Promise<boolean>;
}

export const useMessagesFeedStore = create<MessagesFeedState>((set, get) => ({
  items: [],
  loading: false,
  metadata: defaultMetadata,
  pageInfo: defaultPageInfo,
  client: noopClient,
  loadingMore: false,
  setFeed: ({ items, loading, metadata, pageInfo, client }) =>
    set({ items, loading, metadata, pageInfo, client }),
  reset: () =>
    set({
      items: [],
      loading: false,
      metadata: defaultMetadata,
      pageInfo: defaultPageInfo,
      client: noopClient,
      loadingMore: false,
    }),
  fetch: () => {
    void get().client.fetch();
  },
  fetchNextPage: async () => {
    const { client, pageInfo } = get();

    if (pageInfo.after === null) {
      return true;
    }

    return (await client.fetch({ after: pageInfo.after }))?.status !== 'error';
  },
  fetchUpTo: async (limit) => {
    if (get().loadingMore) {
      return true;
    }

    set({ loadingMore: true });

    try {
      const { client } = get();
      // Cursor and count advance from each RESPONSE, never by re-reading the
      // store: Knock's feed is mirrored in by a React effect, so mid-loop the
      // store still describes the previous page and this would never advance.
      let loaded = get().items.length;
      let after = get().pageInfo.after;

      while (after !== null && loaded < limit) {
        const result = await client.fetch({ after });

        // Knock returns nothing when it declines to issue the request — another
        // fetch is in flight, or the user isn't authenticated. Not a failure.
        if (result === undefined) {
          return true;
        }

        if (result.status === 'error') {
          return false;
        }

        loaded += result.data.entries.length;
        after = result.data.page_info.after;
      }

      return true;
    } finally {
      set({ loadingMore: false });
    }
  },
  markAsRead: (item) => get().client.markAsRead(item),
  markAsArchived: (item) => get().client.markAsArchived(item),
  markAllAsArchived: async () => {
    try {
      return wasBulkAccepted(await get().client.markAllAsArchived());
    } catch {
      return false;
    }
  },
}));
